import { baseApi } from "./baseApi";
import { parseDashboardOverview, parseMutualFundsBrokerListing, parseConsents, parseMutualFundsBrokerSummary, parseMutualFundsDetails, parseMutualFundsAssetAllocation, parseMutualFundsTransactions, parseMutualFundsTransactionDetail, parseBankingAccounts, parseBankingTransactions, parseInsurancePolicies, parseInvestmentPortfolioStocks, parseInvestmentPortfolioStocksDetails, parseInvestmentPortfolioEtf, parseInvestmentPortfolioEtfDetails, parseInvestmentPortfolioInvit, parseInvestmentPortfolioInvitDetails, parseInvestmentPortfolioReit, parseInvestmentPortfolioReitDetails, parseInvestmentPortfolioNps, parseInvestmentPortfolioFDRD, parseInvestmentPortfolioFDRDDetails } from "../parsers/finfactor.parser";
import { parseInvestmentPortfolio } from "../parsers/investmentPortfolio.parser";

export const finfactorApiSlice = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getFinFactorDashboard: builder.query({
            query: () => ({
                url: "api/account-aggregator/dashboard",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return result;
            },
            providesTags: ["Dashboard"]
        }),

        // ─── NEW ENDPOINT ────────────────────────────────────────────────
        getInvestmentPortfolio: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return result;
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioMutualFunds: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/mutual-funds",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return result;
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioStocks: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/stocks",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioStocks(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),

        getConsents: builder.query({
            query: () => ({
                url: "api/account-aggregator/consents",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseConsents(result);
            },
            providesTags: ["Consents"]
        }),
        getMutualFundsBrokerSummary: builder.query({
            query: (brokerId) => ({
                url: `api/account-aggregator/investment-portfolio/mutual-funds/${brokerId}/summary`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseMutualFundsBrokerSummary(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioMutualFundsDetails: builder.query({
            query: (categoryId) => ({
                url: `api/account-aggregator/investment-portfolio/mutual-funds/${categoryId}/funds`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseMutualFundsDetails(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getMutualFundsInvestmentAssetAllocation: builder.query({
            query: (brokerId) => ({
                url: `api/account-aggregator/investment-portfolio/mutual-funds/${brokerId}/asset-allocation`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseMutualFundsAssetAllocation(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getMutualFundsInvestmentTransactions: builder.query({
            query: ({ brokerId, ...params }) => ({
                url: `api/account-aggregator/investment-portfolio/mutual-funds/${brokerId}/transactions`,
                method: "GET",
                params: params
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseMutualFundsTransactions(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getMutualFundsTransactionDetail: builder.query({
            query: (fundId) => ({
                url: `api/account-aggregator/investment-portfolio/mutual-funds/transactions/${fundId}`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseMutualFundsTransactionDetail(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        initiateConsent: builder.mutation({
            query: (body) => ({
                url: "api/account-aggregator/consent/initiate",
                method: "POST",
                body: body
            }),
            invalidatesTags: ["Consents", "Dashboard", "InvestmentPortfolio", "BankingAccounts", "InsurancePolicies"]
        }),
        getBankingAccounts: builder.query({
            query: () => ({
                url: "api/account-aggregator/banking/accounts",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseBankingAccounts(result);
            },
            providesTags: ["BankingAccounts"]
        }),
        getBankingAccountTransactions: builder.query({
            query: (accountId) => ({
                url: `api/account-aggregator/banking/accounts/${accountId}/transactions`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseBankingTransactions(result);
            },
            providesTags: ["BankingAccounts"]
        }),
        getInsurancePolicies: builder.query({
            query: () => ({
                url: "api/account-aggregator/insurance/policies",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInsurancePolicies(result);
            },
            providesTags: ["InsurancePolicies"]
        }),
        getInvestmentPortfolioStocksDetails: builder.query({
            query: (categoryId) => ({
                url: `api/account-aggregator/investment-portfolio/stocks/${categoryId}`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioStocksDetails(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioEtf: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/etf",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioEtf(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioEtfDetails: builder.query({
            query: (categoryId) => ({
                url: `api/account-aggregator/investment-portfolio/etf/${categoryId}`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioEtfDetails(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioInvit: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/invit",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioInvit(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioInvitDetails: builder.query({
            query: (categoryId) => ({
                url: `api/account-aggregator/investment-portfolio/invit/${categoryId}`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioInvitDetails(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioReit: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/reit",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioReit(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioReitDetails: builder.query({
            query: (categoryId) => ({
                url: `api/account-aggregator/investment-portfolio/reit/${categoryId}`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioReitDetails(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioNps: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/nps",
                method: "GET"
            }),
            transformResponse: (response) => {
                const { result } = response || {};
                return parseInvestmentPortfolioNps(result);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioFDRD: builder.query({
            query: () => ({
                url: "api/account-aggregator/investment-portfolio/fd-rd",
                method: "GET"
            }),
            transformResponse: (response) => {
                const data = response?.result || response?.data || response;
                return parseInvestmentPortfolioFDRD(data);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getInvestmentPortfolioFDRDDetails: builder.query({
            query: (id) => ({
                url: `api/account-aggregator/investment-portfolio/fd-rd/${id}`,
                method: "GET"
            }),
            transformResponse: (response) => {
                const data = response?.result || response?.data || response;
                return parseInvestmentPortfolioFDRDDetails(data);
            },
            providesTags: ["InvestmentPortfolio"]
        }),
        getVendorConsentStatus: builder.query({
            query: (consenthandleId) => ({
                url: "api/account-aggregator/vendor/consent-status",
                method: "POST",
                body: { consenthandleId }
            }),
            transformResponse: (response) => {
                const data = response?.result?.data || response?.result || response;
                return data;
            }
        }),
        // ─────────────────────────────────────────────────────────────────
    })
});

export const {
    useGetFinFactorDashboardQuery,
    useLazyGetFinFactorDashboardQuery,
    useGetInvestmentPortfolioQuery,
    useLazyGetInvestmentPortfolioQuery,
    useGetInvestmentPortfolioMutualFundsQuery,
    useLazyGetInvestmentPortfolioMutualFundsQuery,
    useGetInvestmentPortfolioStocksQuery,
    useLazyGetInvestmentPortfolioStocksQuery,
    useGetConsentsQuery,
    useLazyGetConsentsQuery,
    useGetVendorConsentStatusQuery,
    useLazyGetVendorConsentStatusQuery,
    useGetMutualFundsBrokerSummaryQuery,
    useLazyGetMutualFundsBrokerSummaryQuery,
    useGetInvestmentPortfolioMutualFundsDetailsQuery,
    useLazyGetInvestmentPortfolioMutualFundsDetailsQuery,
    useGetMutualFundsInvestmentAssetAllocationQuery,
    useLazyGetMutualFundsInvestmentAssetAllocationQuery,
    useGetMutualFundsInvestmentTransactionsQuery,
    useLazyGetMutualFundsInvestmentTransactionsQuery,
    useGetMutualFundsTransactionDetailQuery,
    useLazyGetMutualFundsTransactionDetailQuery,
    useInitiateConsentMutation,
    useGetBankingAccountsQuery,
    useLazyGetBankingAccountsQuery,
    useGetBankingAccountTransactionsQuery,
    useLazyGetBankingAccountTransactionsQuery,
    useGetInsurancePoliciesQuery,
    useLazyGetInsurancePoliciesQuery,
    useGetInvestmentPortfolioStocksDetailsQuery,
    useLazyGetInvestmentPortfolioStocksDetailsQuery,
    useGetInvestmentPortfolioEtfQuery,
    useLazyGetInvestmentPortfolioEtfQuery,
    useGetInvestmentPortfolioEtfDetailsQuery,
    useLazyGetInvestmentPortfolioEtfDetailsQuery,
    useGetInvestmentPortfolioInvitQuery,
    useLazyGetInvestmentPortfolioInvitQuery,
    useGetInvestmentPortfolioInvitDetailsQuery,
    useLazyGetInvestmentPortfolioInvitDetailsQuery,
    useGetInvestmentPortfolioReitQuery,
    useLazyGetInvestmentPortfolioReitQuery,
    useGetInvestmentPortfolioReitDetailsQuery,
    useLazyGetInvestmentPortfolioReitDetailsQuery,
    useGetInvestmentPortfolioNpsQuery,
    useLazyGetInvestmentPortfolioNpsQuery,
    useGetInvestmentPortfolioFDRDQuery,
    useLazyGetInvestmentPortfolioFDRDQuery,
    useGetInvestmentPortfolioFDRDDetailsQuery,
    useLazyGetInvestmentPortfolioFDRDDetailsQuery,
} = finfactorApiSlice;
